/**
 * Calendar utility functions for generating calendar event links and files
 */

/**
 * Format date to Google Calendar format (YYYYMMDDTHHmmssZ)
 */
function formatGoogleDate(dateStr) {
  const date = new Date(dateStr);
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/**
 * Format date to ICS format (YYYYMMDDTHHmmssZ)
 */
function formatICSDate(dateStr) {
  const date = new Date(dateStr);
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/**
 * Escape text for ICS format
 */
function escapeICS(text) {
  if (!text) return '';
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Generate Google Calendar URL
 */
export function generateGoogleCalendarUrl(event) {
  const { title, timestamp_utc, interpretation, sign, subtitle } = event;
  
  // Create start and end times (1 hour duration for point-in-time events)
  const startDate = new Date(timestamp_utc);
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour later
  
  const start = formatGoogleDate(startDate.toISOString());
  const end = formatGoogleDate(endDate.toISOString());
  
  // Build description
  let description = '';
  if (subtitle) description += `${subtitle}\n\n`;
  if (interpretation) description += `${interpretation}\n\n`;
  description += `Tropical Zodiac signs with sidereal nakshatras (True Lahiri Ayanamsa)\nvia DailyNakshatra.com`;
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `🌟 ${title}`,
    dates: `${start}/${end}`,
    details: description,
    location: sign ? `${sign} (Sidereal)` : '',
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generate Outlook Calendar URL
 */
export function generateOutlookCalendarUrl(event) {
  const { title, timestamp_utc, interpretation, sign, subtitle } = event;
  
  const startDate = new Date(timestamp_utc);
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
  
  let description = '';
  if (subtitle) description += `${subtitle}\n\n`;
  if (interpretation) description += `${interpretation}\n\n`;
  description += `Tropical Zodiac signs with sidereal nakshatras (True Lahiri Ayanamsa)\nvia DailyNakshatra.com`;
  
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: `🌟 ${title}`,
    startdt: startDate.toISOString(),
    enddt: endDate.toISOString(),
    body: description,
    location: sign ? `${sign} (Sidereal)` : '',
  });
  
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * Generate ICS file content
 */
export function generateICSContent(event) {
  const { title, timestamp_utc, interpretation, sign, subtitle, id } = event;
  
  const startDate = new Date(timestamp_utc);
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
  
  const start = formatICSDate(startDate.toISOString());
  const end = formatICSDate(endDate.toISOString());
  const now = formatICSDate(new Date().toISOString());
  
  let description = '';
  if (subtitle) description += `${subtitle}\\n\\n`;
  if (interpretation) description += `${interpretation}\\n\\n`;
  description += `Tropical Zodiac signs with sidereal nakshatras (True Lahiri Ayanamsa)\\nvia DailyNakshatra.com`;
  
  const uid = `${id || Date.now()}@dailynakshatra.com`;
  
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//DailyNakshatra//Astrological Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:🌟 ${escapeICS(title)}`,
    `DESCRIPTION:${escapeICS(description)}`,
    sign ? `LOCATION:${escapeICS(sign)} (Sidereal)` : '',
    'STATUS:CONFIRMED',
    'TRANSP:TRANSPARENT',
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapeICS(title)} is happening soon`,
    'TRIGGER:-P1D',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean).join('\r\n');
  
  return icsContent;
}

/**
 * Download ICS file
 */
export function downloadICSFile(event) {
  const content = generateICSContent(event);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generate Yahoo Calendar URL
 */
export function generateYahooCalendarUrl(event) {
  const { title, timestamp_utc, interpretation, sign, subtitle } = event;
  
  const startDate = new Date(timestamp_utc);
  const start = formatGoogleDate(startDate.toISOString());
  
  let description = '';
  if (subtitle) description += `${subtitle}\n\n`;
  if (interpretation) description += `${interpretation}\n\n`;
  description += `Tropical Zodiac signs with sidereal nakshatras (True Lahiri Ayanamsa)\nvia DailyNakshatra.com`;
  
  const params = new URLSearchParams({
    v: '60',
    title: `🌟 ${title}`,
    st: start,
    dur: '0100', // 1 hour
    desc: description,
    in_loc: sign ? `${sign} (Sidereal)` : '',
  });
  
  return `https://calendar.yahoo.com/?${params.toString()}`;
}
